import { supabase } from '@/lib/supabase/client';

// ---------------------------------------------------------------------------
// Types — mirror the edge function response
// ---------------------------------------------------------------------------

export interface TriagedIssue {
  id: string;
  shortId: string;
  title: string;
  culprit: string;
  level: string;
  count: number;
  userCount: number;
  firstSeen: string;
  lastSeen: string;
  status: string;
  isUnhandled: boolean;
  permalink: string;
  category: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  priorityScore: number;
  suggestedAction: string;
  affectedFile: string | null;
  agentPrompt: string;
}

export interface TriageSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  topCategory: string;
  lastUpdated: string;
}

export interface SentryTriageResponse {
  issues: TriagedIssue[];
  summary: TriageSummary;
}

// ---------------------------------------------------------------------------
// Agent export format
// ---------------------------------------------------------------------------

interface AgentExportIssue {
  id: string;
  title: string;
  category: string;
  priority: string;
  priorityScore: number;
  eventCount: number;
  userCount: number;
  firstSeen: string;
  lastSeen: string;
  affectedFile: string | null;
  culprit: string;
  sentryUrl: string;
  suggestedAction: string;
  agentPrompt: string;
}

interface AgentExport {
  generated: string;
  project: string;
  sentryOrg: string;
  totalIssues: number;
  issues: AgentExportIssue[];
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function fetchSentryTriage(
  query = 'is:unresolved',
  limit = 50,
): Promise<SentryTriageResponse> {
  // Ensure fresh JWT before calling admin-only edge function
  await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('sentry-issues');

  if (error) {
    let detail = error.message || 'Unknown error';
    if (error.context instanceof Response) {
      try {
        const body = await error.context.json();
        detail = body?.error || detail;
      } catch { /* response already consumed */ }
    }
    throw new Error(detail);
  }

  return data as SentryTriageResponse;
}

// ---------------------------------------------------------------------------
// Export helpers
// ---------------------------------------------------------------------------

export function buildAgentExportJson(issues: TriagedIssue[]): AgentExport {
  return {
    generated: new Date().toISOString(),
    project: 'turn-platform',
    sentryOrg: 'turn-o2',
    totalIssues: issues.length,
    issues: issues.map((i) => ({
      id: i.id,
      title: i.title,
      category: i.category,
      priority: i.priority,
      priorityScore: i.priorityScore,
      eventCount: i.count,
      userCount: i.userCount,
      firstSeen: i.firstSeen,
      lastSeen: i.lastSeen,
      affectedFile: i.affectedFile,
      culprit: i.culprit,
      sentryUrl: i.permalink,
      suggestedAction: i.suggestedAction,
      agentPrompt: i.agentPrompt,
    })),
  };
}

export async function copyAgentJson(issues: TriagedIssue[]): Promise<void> {
  const payload = buildAgentExportJson(issues);
  await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
}
